import { useState } from 'react'

import { useUnit } from 'effector-react'
import { useQueryClient } from 'react-query'

import { $creatorId } from '@/models/creators'
import { toast } from '@/models/toasts'
import { useAccount } from '@/modules/accounts/hooks/useAccounts'
import { Audio, UploadFileRequestOptions } from '@/modules/chatting/types'
import { BACKEND_URL } from '@/utils/constants'

export const useUploadAudio = ({ onUpload }: { onUpload: () => void }) => {
  const creatorId = useUnit($creatorId) ?? 0
  const { accessToken } = useAccount()
  const [uploadProgress, setUploadProgress] = useState(0)
  const queryClient = useQueryClient()

  const uploadAudio = async ({
    file,
    name,
    description,
  }: UploadFileRequestOptions) => {
    const formData = new FormData()

    formData.append('file', file)
    formData.append('name', name)
    if (description?.length) {
      formData.append('description', description)
    }
    const xhr = new XMLHttpRequest()

    xhr.open('POST', `${BACKEND_URL}/chat-audio/${creatorId}`)
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
    xhr.upload.onprogress = (event) => {
      const progress = Math.round((event.loaded / event.total) * 100)

      setUploadProgress(progress)
    }
    xhr.withCredentials = true
    xhr.send(formData)
    const uploadResponse = await new Promise<XMLHttpRequest>((resolve) => {
      const responseHandler = () => {
        resolve(xhr)
      }

      xhr.onload = responseHandler
      xhr.onerror = responseHandler
    })
    let errorMessage = ''

    if (uploadResponse.status === 409) {
      // Conflict
      errorMessage = 'This audio was already uploaded.'
    }

    if (uploadResponse.status === 423) {
      // Blocked
      errorMessage = 'Somebody else is uploading an audio right now'
    }

    if (uploadResponse.status === 413) {
      // Payload too large
      errorMessage = 'Your audio has to be less than 20MB!'
    }

    if (uploadResponse.status !== 200) {
      // Other error
      errorMessage = errorMessage
        ? errorMessage
        : 'Error ' + uploadResponse.status

      toast.error(errorMessage)

      setUploadProgress(0)
      throw new Error(errorMessage)
    }

    const newAudio = await JSON.parse(uploadResponse.response)

    queryClient.setQueryData<Audio[] | undefined>(
      ['audios', creatorId],
      (oldData) => {
        if (!oldData) return oldData

        const newAudiosArray = [...oldData, newAudio].sort((o1, o2) =>
          o1.name.localeCompare(o2.name),
        )

        return newAudiosArray
      },
    )
    onUpload()
    toast.success('Successfully uploaded audio.')
  }

  return {
    uploadAudio,
    uploadProgress,
    setUploadProgress,
  }
}
